/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import {
	View,
	Icon,
	Pressable,
	DraggableView,
	DraggablePositionType,
} from '@components/atoms'

import { SignPositionOffsetType } from '@schemas'
import { deepPurple, grey } from '@mui/material/colors'

interface PropsType {
	mode?: 'none' | 'add' | 'update' | 'sign'
	showCloseButton?: boolean
	showSubmitButton?: boolean
	showSettingButton?: boolean
	onStop: (e: DraggablePositionType) => void
	onSubmit: () => void
	onClose: () => void
	onSetting?: () => void
	children?: JSX.Element
	customButton?: JSX.Element
	disabled?: boolean
	positionOffset?: SignPositionOffsetType
	showSignature?: boolean
	onSignatureClick?: () => void
	isSignature?: boolean
	hide?: boolean
}

export const PdfDraggableView = (props: PropsType) => {
	const { onStop, onSubmit, onClose, children, onSetting } = props

	const mode: 'none' | 'add' | 'update' | 'sign' = props.mode ?? 'none'
	const isSignature = props.isSignature ?? false
	const showSettingButton = props.showSettingButton ?? false
	const showCloseButton = props.showCloseButton ?? true
	const showSubmitButton = props.showSubmitButton ?? true
	const showSignature = props.showSignature ?? false
	const hide = props.hide ?? false

	const onSignatureClick = () => {
		if (props.onSignatureClick) {
			props.onSignatureClick()
		}
	}

	return (
		<DraggableView
			onStop={onStop}
			disabled={props.disabled}
			positionOffset={props.positionOffset}
		>
			<View
				css={[style, { display: hide ? 'none' : 'block' }]}
				className={
					mode === 'sign'
						? 'signMode'
						: isSignature
						? 'signature'
						: showSignature
						? 'showSignature'
						: props.disabled
						? 'disabled'
						: ''
				}
			>
				{!isSignature && !showSignature && (
					<View className="pdfDraggableButtonView">
						{props.customButton ?? props.customButton}
						{showSettingButton && (
							<Pressable className="pdfDraggableButton submit" onClick={onSetting}>
								<Icon name="cog" className="pdfDraggableIcon" />
							</Pressable>
						)}
						{showSubmitButton && (
							<Pressable className="pdfDraggableButton submit" onClick={onSubmit}>
								<Icon name="check" className="pdfDraggableIcon" />
							</Pressable>
						)}
						{showCloseButton && (
							<Pressable className="pdfDraggableButton close" onClick={onClose}>
								<Icon name="close" className="pdfDraggableIcon" />
							</Pressable>
						)}
					</View>
				)}

				{showSignature && (
					<Pressable className="signatureButton" onClick={onSignatureClick} />
				)}
				{children}
			</View>
		</DraggableView>
	)
}

const style = css({
	position: 'absolute',
	zIndex: 100000,
	background: '#dcc4ff8c',
	border: '2px solid',
	borderColor: deepPurple['A400'],
	borderRadius: '5px',
	cursor: 'move',

	'&.disabled': {
		cursor: 'default',
		background: '#b7b7b78a',
		borderColor: grey[700],
	},

	'&.showSignature': {
		cursor: 'auto',
	},

	'&.signature': {
		cursor: 'default',
	},

	'&.signMode': {
		border: 0,
		background: '#e5e5e5b4',
	},

	'.signatureButton': {
		position: 'absolute',
		zIndex: 5,
		width: '100%',
		height: '100%',
		top: 0,
		left: 0,
	},

	'.pdfDraggableButtonView': {
		position: 'absolute',
		zIndex: 5,
		right: 0,
		display: 'inline-block',

		'.pdfDraggableButton': {
			display: 'inline-block',
			cursor: 'pointer',
			padding: 4,
			opacity: 0.7,

			'&.submit': {
				backgroundColor: deepPurple['A400'],
			},

			'&.close': {
				backgroundColor: deepPurple['A400'],
			},

			'.pdfDraggableIcon': {
				color: 'white',
			},
		},
	},
})
